import { useCallback, use, useMemo } from "react";
import { useI18n } from "./i18n-hooks";
import { useMutation } from "@apollo/client/react";
import { DELETE_CURRENT_PROFILE } from "../graphql/identity";
import { useSnackbar } from "./snackbar";
import { ProfileType } from "../__generated__/graphql";
import { AuthManagerContext } from "../contexts/AuthManager";
import { KEYSTORE_DEFAULT_PROFILE_ID } from "../contexts/ProfileSwitcherManager";
import { safeAsyncStorage } from "../utils/safe-async-storage";
import { useSetAlert } from "./use-set-alert";

type UseDeleteCurrentProfileProps = {
    profileType: ProfileType;
    isPrivateClient?: boolean;
    profileId?: string;
};

export const useDeleteCurrentProfile = ({
    profileType,
    isPrivateClient = false,
    profileId,
}: UseDeleteCurrentProfileProps) => {
    const i18n = useI18n();
    const { setAlert } = useSetAlert();
    const [deleteCurrentProfile] = useMutation(
        DELETE_CURRENT_PROFILE,
        profileId ? { context: { liftProfile: profileId } } : undefined,
    );
    const { showSnackbar } = useSnackbar();
    const { refreshUser } = use(AuthManagerContext);
    const fullProfileType = useMemo(
        () => `${isPrivateClient ? "private_" : ""}${profileType}`,
        [isPrivateClient, profileType],
    );

    const onDeleteCurrentProfile = useCallback(() => {
        return new Promise((resolve) => {
            setAlert({
                title: i18n.t(`profile.delete.${fullProfileType}.title`),
                message: i18n.t(`profile.delete.${fullProfileType}.description`),
                buttons: [
                    {
                        text: i18n.t(`profile.delete.${fullProfileType}.confirm`),
                        onPress: () => {
                            const asyncFunc = async () => {
                                const deleteResult = await deleteCurrentProfile();

                                if (deleteResult.data?.deleteCurrentProfile) {
                                    void safeAsyncStorage.removeItem(KEYSTORE_DEFAULT_PROFILE_ID);
                                    const newUser = await refreshUser?.();

                                    if (!newUser) {
                                        return;
                                    }

                                    showSnackbar({
                                        text: i18n.t(`profile.delete.${fullProfileType}.success`),
                                        mode: "success",
                                    });
                                    resolve(null);
                                } else {
                                    setAlert({
                                        title: i18n.t("profile.delete_account_error"),
                                        message: i18n.t(
                                            `profile.delete.${fullProfileType}.error_description`,
                                        ),
                                    });
                                    resolve(null);
                                }
                            };
                            void asyncFunc();
                        },
                        style: "destructive",
                    },
                    {
                        text: i18n.t("cancel"),
                        style: "cancel",
                    },
                ],
                options: { cancelable: true },
            });
        });
    }, [setAlert, i18n, fullProfileType, deleteCurrentProfile, refreshUser, showSnackbar]);

    return onDeleteCurrentProfile;
};
